import { bffQuery, bffMutation } from '../lib/bffClient';

/**
 * «پیش‌پرداخت مراجعه» — the desk's writes and the one read (PM-0657, contract
 * §5.2 / §6.3). Plain functions rather than a stateful hook: the booking
 * dialogs, the row dialog and the settle drawer each own their own state, and
 * a second copy of the deposit held here would be a second answer to «how much
 * is held».
 *
 * MONEY: minor units of the clinic's currency on the wire (rials for IRR).
 * Every write takes a `requestId` the caller keeps across retries, so a retry
 * after NO_OPEN_SHIFT records ONE deposit, not two.
 */

const DEPOSIT_FIELDS = `
  appointmentId state closeReason
  depositedRial appliedRial refundedRial forfeitedRial heldRial releasedRial refundableRial
  invoiceId feeInvoiceId refundWindowHours refundDeadline noShowOutcome noShowFeePercent currency
`;

const RESULT_FIELDS = `
  currency
  deposit { ${DEPOSIT_FIELDS} }
  credit { id amountRial source paymentMethod currency }
`;

export const RECORD_APPOINTMENT_DEPOSIT = `
  mutation RecordAppointmentDeposit(
    $appointmentId: ID!, $amountRial: Float!, $paymentMethod: String,
    $referenceNumber: String, $note: String, $accountId: ID, $requestId: ID
  ) {
    recordAppointmentDeposit(
      appointmentId: $appointmentId, amountRial: $amountRial, paymentMethod: $paymentMethod,
      referenceNumber: $referenceNumber, note: $note, accountId: $accountId, requestId: $requestId
    ) { ${RESULT_FIELDS} }
  }
`;

export const RELEASE_APPOINTMENT_DEPOSIT = `
  mutation ReleaseAppointmentDeposit($appointmentId: ID!, $reason: String) {
    releaseAppointmentDeposit(appointmentId: $appointmentId, reason: $reason) { ${RESULT_FIELDS} }
  }
`;

export const REFUND_APPOINTMENT_DEPOSIT = `
  mutation RefundAppointmentDeposit(
    $appointmentId: ID!, $amountRial: Float, $paymentMethod: String,
    $referenceNumber: String, $accountId: ID, $reason: String!, $requestId: ID
  ) {
    refundAppointmentDeposit(
      appointmentId: $appointmentId, amountRial: $amountRial, paymentMethod: $paymentMethod,
      referenceNumber: $referenceNumber, accountId: $accountId, reason: $reason, requestId: $requestId
    ) { ${RESULT_FIELDS} }
  }
`;

// The row dialog reads the SETTLEMENT, not a query of its own (§6.3): the BFF's
// `buildReceptionSettlement` asks accounting with the rows included, the day
// loader behind `Appointment.deposit` never does.
export const APPOINTMENT_DEPOSIT_QUERY = `
  query AppointmentDeposit($appointmentId: ID!) {
    receptionSettlement(appointmentId: $appointmentId) {
      currency
      creditBalanceRial
      deposit {
        ${DEPOSIT_FIELDS}
        rows { id amountRial source paymentMethod note createdAt paymentId currency }
      }
    }
  }
`;

/** A request id for one deposit act, kept by the caller across retries. */
export const newDepositRequestId = (prefix = 'appt-deposit') =>
  `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

export async function recordAppointmentDeposit({
  appointmentId, amountRial, paymentMethod, referenceNumber, note, accountId, requestId,
}) {
  const data = await bffMutation(RECORD_APPOINTMENT_DEPOSIT, {
    appointmentId,
    amountRial,
    paymentMethod: paymentMethod || 'CASH',
    referenceNumber: referenceNumber || null,
    note: note || null,
    accountId: accountId || null,
    requestId: requestId || null,
  }, 'RecordAppointmentDeposit');
  return data?.recordAppointmentDeposit || null;
}

export async function releaseAppointmentDeposit({ appointmentId, reason }) {
  const data = await bffMutation(RELEASE_APPOINTMENT_DEPOSIT, {
    appointmentId, reason: reason || null,
  }, 'ReleaseAppointmentDeposit');
  return data?.releaseAppointmentDeposit || null;
}

export async function refundAppointmentDeposit({
  appointmentId, amountRial, paymentMethod, referenceNumber, accountId, reason, requestId,
}) {
  const data = await bffMutation(REFUND_APPOINTMENT_DEPOSIT, {
    appointmentId,
    // 0 / null = everything refundable (§3.3).
    amountRial: amountRial || null,
    paymentMethod: paymentMethod || 'CASH',
    referenceNumber: referenceNumber || null,
    accountId: accountId || null,
    reason,
    requestId: requestId || null,
  }, 'RefundAppointmentDeposit');
  return data?.refundAppointmentDeposit || null;
}

/** `{ deposit, creditBalanceRial, currency }` for one appointment. */
export async function loadAppointmentDeposit(appointmentId) {
  const data = await bffQuery(APPOINTMENT_DEPOSIT_QUERY, { appointmentId }, 'AppointmentDeposit');
  const s = data?.receptionSettlement || null;
  return {
    deposit: s?.deposit || null,
    creditBalanceRial: s?.creditBalanceRial ?? null,
    currency: s?.currency || s?.deposit?.currency || null,
  };
}
